import React, { useState } from 'react';
import { Ban, ThumbsDown, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

/**
 * ExercisePreferenceButtons - Visible text buttons to block exercise/equipment
 * 
 * Works in two modes:
 * 1. With workout context (workoutPlanId, dayIndex, exerciseIndex provided):
 *    - Calls /api/workouts/replace-exercise to REPLACE and BLOCK
 *    - Same behavior as the 3-dot menu
 * 2. Without workout context (from Exercise Library):
 *    - Only saves preference for future workouts
 */
const ExercisePreferenceButtons = ({ 
  exercise,
  workoutPlanId,
  dayIndex,
  exerciseIndex,
  onExerciseReplaced,
  onPreferenceUpdated,
  className = ""
}) => {
  const { api } = useAuth();
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  if (!exercise) return null;

  // Check if we have workout context for replacement
  const hasWorkoutContext = workoutPlanId !== undefined && dayIndex !== undefined && exerciseIndex !== undefined;

  const exerciseName = exercise?.name || exercise?.exercise || 'this exercise';
  const equipmentType = exercise?.equipment || exercise?.equipment_type || null;

  const handleAction = async (reason) => {
    setIsProcessing(true);
    setConfirmDialog(null);

    try {
      if (hasWorkoutContext) {
        // MODE 1: Replace exercise in workout (same as 3-dot menu)
        const response = await api.post('/workouts/replace-exercise', {
          workout_plan_id: workoutPlanId,
          day_index: dayIndex,
          exercise_index: exerciseIndex,
          reason: reason
        });

        if (response.data.replaced) {
          toast.success(response.data.message || "Exercise replaced!", {
            duration: 3000,
          });

          try {
            if (onExerciseReplaced) {
              onExerciseReplaced({
                oldExercise: response.data.old_exercise,
                newExercise: response.data.new_exercise,
                updatedDay: response.data.updated_day,
                dayIndex: dayIndex,
                exerciseIndex: exerciseIndex
              });
            }
          } catch (cbErr) {
            console.error('[PreferenceButtons] Callback error:', cbErr);
            // Don't crash — the replacement was saved server-side
            window.location.reload();
          }
        } else {
          if (response.data.suggest_regeneration) {
            toast.error(response.data.error || "Too many swaps — please regenerate the workout.", {
              duration: 4000,
            });
          } else {
            toast.error(response.data.error || "No suitable alternative found.", {
              duration: 4000,
            });
          }
        }
      } else {
        // MODE 2: Just save preference (from Exercise Library)
        if (reason === 'dislike') {
          const response = await api.post('/user/preferences/block-exercise', {
            exercise_id: exercise?._id || exercise?.id,
            exercise_name: exerciseName
          });

          if (response.data.success) {
            toast.success(`Got it — "${exerciseName}" won't appear in your workouts.`, {
              duration: 3000,
            });
            onPreferenceUpdated?.({ type: 'dislike', exercise: exerciseName });
          } else {
            toast.error(response.data.error || "Failed to update preferences.", {
              duration: 4000,
            });
          }
        } else if (reason === 'no_equipment') {
          const response = await api.post('/user/preferences/block-equipment', {
            equipment_type: equipmentType
          });

          if (response.data.success) {
            toast.success(`Got it — exercises requiring "${equipmentType}" won't appear in your workouts.`, {
              duration: 3000,
            });
            onPreferenceUpdated?.({ type: 'no_equipment', equipment: equipmentType });
          } else {
            toast.error(response.data.error || "Failed to update preferences.", {
              duration: 4000,
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to process action:', error);
      toast.error(error.response?.data?.detail || "Failed to process. Please try again.", {
        duration: 4000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getConfirmationContent = () => {
    if (confirmDialog === 'no_equipment') {
      return {
        title: "Block This Equipment?",
        description: hasWorkoutContext
          ? `This will permanently hide all "${equipmentType}" exercises from your workouts. A suitable alternative will be selected immediately.`
          : `Exercises requiring "${equipmentType}" will be excluded from your future workouts. You can change this anytime in Settings.`
      };
    }
    if (confirmDialog === 'dislike') {
      return {
        title: "Block This Exercise?",
        description: hasWorkoutContext
          ? `"${exerciseName}" will be permanently hidden from your workouts. A suitable alternative will be selected immediately.`
          : `"${exerciseName}" will be excluded from your future workouts. You can change this anytime in Settings.`
      };
    }
    return { title: '', description: '' };
  };

  const dialogContent = getConfirmationContent();

  return (
    <>
      <div className={`space-y-3 ${className}`}>
        {/* No access to equipment button */}
        {equipmentType && equipmentType !== 'bodyweight' && equipmentType !== 'none' && (
          <button
            className="w-full py-3.5 px-5 rounded-xl bg-[#1A1A1A] border border-white/10 text-white/70 font-heading font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2.5 active:scale-[0.97] transition-all hover:border-white/20 hover:text-white/90 disabled:opacity-50"
            onClick={() => setConfirmDialog('no_equipment')}
            disabled={isProcessing}
            data-testid="block-equipment-btn"
          >
            {isProcessing && confirmDialog === 'no_equipment' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Ban className="h-4 w-4" />
            )}
            No Access To This Equipment
          </button>
        )}

        {/* I don't like this exercise button */}
        <button
          className="w-full py-3.5 px-5 rounded-xl bg-[#1A1A1A] border border-white/10 text-white/70 font-heading font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2.5 active:scale-[0.97] transition-all hover:border-white/20 hover:text-white/90 disabled:opacity-50"
          onClick={() => setConfirmDialog('dislike')}
          disabled={isProcessing}
          data-testid="block-exercise-btn"
        >
          {isProcessing && confirmDialog === 'dislike' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ThumbsDown className="h-4 w-4" />
          )}
          I Don't Like This Exercise
        </button>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {confirmDialog === 'dislike' ? (
                <ThumbsDown className="h-5 w-5 text-red-400" />
              ) : (
                <Ban className="h-5 w-5 text-orange-400" />
              )}
              {dialogContent.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              {dialogContent.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="preference-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleAction(confirmDialog)}
              className="bg-accent text-accent-foreground hover:bg-accent/90 font-medium"
              data-testid="preference-confirm-btn"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {hasWorkoutContext ? 'Replacing...' : 'Saving...'}
                </>
              ) : (
                'Confirm'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ExercisePreferenceButtons;
